import { PartnerBranding } from "app/types/services/branding";

// Ported from the customer portal's utils/apply-branding.ts and adapted to checkout.

export const DEFAULT_FAVICON = "/favicon.ico";
export const DEFAULT_TITLE = "Relm Checkout";
export const DEFAULT_BRAND_NAME = "Relm";
export const DEFAULT_LOGO = "/relm-logo.svg";
export const DEFAULT_SUPPORT_EMAIL = "support@relm.co";
/** Checkout's existing accent; the CSS defaults in globals.css match it. */
export const DEFAULT_BRAND_COLOR = "#7C5CDB";

type Rgb = [number, number, number];

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];


const BRAND_SHADES: Array<[string, Rgb, number]> = [
  ["--brand-end-rgb", WHITE, 0.6], //      #4F8CFF  gradient end
  ["--brand-soft-rgb", WHITE, 0.5], //     #B9A4FF  borders / hover
  ["--brand-tint-rgb", WHITE, 0.4], //     #C9B8FF  underline / ring
  ["--brand-pale-rgb", WHITE, 0.28], //    #D9CCFF  dividers / card border
  ["--brand-end-pale-rgb", WHITE, 0.22], // #A9D4FF light gradient end
  ["--brand-faint-rgb", WHITE, 0.12], //   #EDE7FF  very light tint
  ["--brand-dark-rgb", BLACK, 0.55], //    #3d2a66  CTA / emphasis text
  ["--brand-darker-rgb", BLACK, 0.42], //  #2f1f52  CTA hover
];

const ALL_BRAND_VARS = ["--brand-color", "--brand-rgb", ...BRAND_SHADES.map(([v]) => v)];

export function parseColor(input: string | null | undefined): Rgb | null {
  if (!input) return null;
  const value = input.trim();

  const hex = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }

  const rgb = value.match(/^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i);
  if (rgb) {
    const parts = [rgb[1], rgb[2], rgb[3]].map((n) => Math.min(255, Number(n)));
    return parts as Rgb;
  }

  return null;
}

const mix = (a: Rgb, b: Rgb, weightOfA: number): Rgb =>
  a.map((c, i) => Math.round(c * weightOfA + b[i] * (1 - weightOfA))) as Rgb;

const triplet = ([r, g, b]: Rgb) => `${r} ${g} ${b}`;

export function hasPartnerBranding(branding: PartnerBranding | null): boolean {
  return Boolean(branding?.partnerId || branding?.brandName || branding?.brandLogoUrl);
}

export function resolveBrandIcon(branding: PartnerBranding | null): string {
  return branding?.brandFaviconUrl || branding?.brandLogoUrl || DEFAULT_FAVICON;
}

/** Prefixes the tab title with the non-prod environment (detected from the host or backend URL). */
export function withEnvPrefix(title: string): string {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const source = `${host} ${window.__RUNTIME_CONFIG__?.REACT_APP_BASE_URL ?? ""}`.toLowerCase();

  if (source.includes("preprod")) return `[Preprod] ${title}`;
  if (source.includes("staging")) return `[Staging] ${title}`;
  return title;
}

const MIME_BY_EXT: Record<string, string> = {
  ico: "image/x-icon",
  png: "image/png",
  svg: "image/svg+xml",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
};

export function getIconType(url: string): string | undefined {
  try {
    const pathname = url.startsWith("http") ? new URL(url).pathname : url;
    const ext = pathname.split(".").pop()?.toLowerCase();
    return ext ? MIME_BY_EXT[ext] : undefined;
  } catch {
    return undefined;
  }
}

function applyBrandColor(branding: PartnerBranding | null): void {
  const root = document.documentElement;
  const rgb = parseColor(branding?.brandPrimaryColor);

  if (!rgb) {
    // No (valid) partner colour → fall back to the CSS defaults in globals.css.
    ALL_BRAND_VARS.forEach((v) => root.style.removeProperty(v));
    return;
  }

  root.style.setProperty("--brand-color", `rgb(${rgb.join(", ")})`);
  root.style.setProperty("--brand-rgb", triplet(rgb));
  BRAND_SHADES.forEach(([cssVar, base, weight]) => root.style.setProperty(cssVar, triplet(mix(rgb, base, weight))));
}

const ICON_SELECTOR = "link[rel~='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']";

function applyFavicon(branding: PartnerBranding | null): void {
  const iconUrl = resolveBrandIcon(branding);
  const type = getIconType(iconUrl);
  const links = document.querySelectorAll<HTMLLinkElement>(ICON_SELECTOR);

  if (links.length === 0) {
    const link = document.createElement("link");
    link.rel = "icon";
    link.href = iconUrl;
    if (type) link.type = type;
    document.head.appendChild(link);
    return;
  }

  links.forEach((link) => {
    // Keep the bundled apple-touch-icon when the partner has no icon of their own.
    if (!hasPartnerBranding(branding) && link.rel === "apple-touch-icon") return;
    if (link.getAttribute("href") !== iconUrl) link.setAttribute("href", iconUrl);
    if (type) link.setAttribute("type", type);
    else link.removeAttribute("type");
  });

  // Safari pinned-tab mask is Relm's mark; drop it for partners.
  if (hasPartnerBranding(branding)) {
    document.querySelectorAll("link[rel='mask-icon']").forEach((el) => el.remove());
  }
}

/** Applies partner branding (colour, favicon, tab title) to the document. `null` = Relm defaults. */
export function applyBrandingToDocument(branding: PartnerBranding | null): void {
  if (typeof document === "undefined") return;

  const isPartner = hasPartnerBranding(branding);

  if (isPartner) {
    document.documentElement.setAttribute("data-partner-branding", "true");
  } else {
    document.documentElement.removeAttribute("data-partner-branding");
  }

  applyBrandColor(branding);
  applyFavicon(branding);

  const title = isPartner && branding?.brandName ? `${branding.brandName} Checkout` : DEFAULT_TITLE;
  document.title = withEnvPrefix(title);
}
